import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useImpersonation } from './useImpersonation';

interface EffectiveProfile {
  id: string;
  role: 'client' | 'vendor' | 'relay' | 'admin' | 'hr' | 'super_admin';
  system_role: 'client' | 'vendor' | 'relay' | 'admin' | 'hr' | 'super_admin';
  full_name: string | null;
  business_name?: string | null;
  phone: string | null;
  avatar_url: string | null;
  vendor_status: 'pending' | 'approved' | 'rejected' | null;
  email?: string;
}

/**
 * Hook qui retourne le profil de l'utilisateur effectif (impersonifié ou réel)
 */
export const useEffectiveProfile = () => {
  const { user, profile: realProfile } = useAuth();
  const { isImpersonating, getEffectiveUserId, impersonatedUserEmail } = useImpersonation();
  const [effectiveProfile, setEffectiveProfile] = useState<EffectiveProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEffectiveProfile = async () => {
      const effectiveUserId = getEffectiveUserId();
      
      if (!effectiveUserId) {
        setEffectiveProfile(null);
        setLoading(false);
        return;
      }

      // Si pas d'impersonation, utiliser le profil réel
      if (!isImpersonating) {
        setEffectiveProfile(realProfile ? {
          ...realProfile,
          email: user?.email
        } : null);
        setLoading(false);
        return;
      }

      // Charger le profil de l'utilisateur impersonifié
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', effectiveUserId)
          .single();

        if (error) throw error;

        // Récupérer le rôle le plus élevé depuis user_roles
        const { data: userRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', effectiveUserId)
          .order('role', { ascending: true })
          .limit(1)
          .maybeSingle();

        const effectiveRole = userRole?.role || data.role || 'client';
        setEffectiveProfile({
          ...data,
          role: effectiveRole,
          system_role: effectiveRole,
          email: impersonatedUserEmail || undefined
        });
      } catch (error) {
        console.error('Error loading effective profile:', error);
        setEffectiveProfile(null);
      } finally {
        setLoading(false);
      }
    };

    loadEffectiveProfile();
  }, [isImpersonating, getEffectiveUserId, realProfile, user, impersonatedUserEmail]);

  return {
    profile: effectiveProfile,
    loading,
    isImpersonating
  };
};
